import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Collapse, Divider, Typography } from "@mui/material";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import { alpha, styled } from "@mui/material/styles";
import React, { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";

import NavItem from "./NavItem";
import NavItemSkeleton from "./NavItemSkeleton";
import { COLLAPSED_DIVIDER_WIDTH, hasOnlyChild, NAV_ICON_GLYPH_SIZE, NAVIGATION_DOM_IDS, NAVIGATION_LABELS, NAVIGATION_TEST_IDS } from "./NavigationBar.consts";
import { trackEvent, ANALYTICS_EVENTS } from "@analytics";
import { useIsAdminPanel, useIsMobile, useStatusBarDisplayed } from "@hooks";
import useAppStore from "../../stores/appStore";
import { systemModeSelector } from "../../stores/selectors/appSelectors";
import { SYSTEM_MODES } from "../../stores/slices/createAppSlice";
import AssistantSelector from "../AssistantSelector";
import { APP_HEADER_HEIGHT, APP_MOBILE_HEADER_HEIGHT, APP_STATUS_BAR_HEIGHT } from "../consts";

const ChevronIcon = styled(IconButton, {
  shouldForwardProp: prop => prop !== "statusBarDisplayed"
})(({ theme, statusBarDisplayed }) => ({
  "zIndex": 1,
  "position": "absolute",
  "padding": 0,
  "top": statusBarDisplayed ? 145 : 116,
  "right": "-13px",
  "backgroundColor": theme.palette.system[0],
  "border": `1px solid ${alpha(theme.palette.text[800], 0.24)}`,
  "&:hover": {
    backgroundColor: theme.palette.system[50]
  },
  [theme.breakpoints.down("sm")]: {
    display: "none"
  }
}));

const TextDivider = styled("div", {
  shouldForwardProp: prop => prop !== "open" && prop !== "isFirst"
})(({ theme, open, isFirst }) => ({
  color: theme.palette.text[700],
  padding: `${theme.spacing(isFirst ? 1 : 2)} ${theme.spacing(3)} ${theme.spacing(0.5)} ${theme.spacing(3)}`,
  ...(!open && {
    padding: `${theme.spacing(2)} 0`
  })
}));

const CollapsedDivider = styled(Divider)({
  width: COLLAPSED_DIVIDER_WIDTH,
  marginLeft: "auto",
  marginRight: "auto"
});

const SectionTitle = styled(Typography)({
  color: "inherit",
  letterSpacing: "1px"
});

const SectionToggle = styled(ButtonBase)(({ theme }) => ({
  "gap": theme.spacing(1),
  "width": "100%",
  "justifyContent": "flex-start",
  "color": "inherit",
  "borderRadius": theme.shape.borderRadius,
  "&:hover": {
    color: theme.palette.text[800]
  },
  "svg": {
    width: NAV_ICON_GLYPH_SIZE,
    height: NAV_ICON_GLYPH_SIZE
  }
}));

const DrawerContent = (
  {
    open,
    setOpen,
    sections,
    collapsedSections = [],
    toggleSection = () => {},
    showAssistantSelector,
    useCurrentPage,
    showMinimizeButton = true
  }
) => {
  const systemMode = useAppStore(systemModeSelector);
  const currentPage = useCurrentPage();
  const isMobile = useIsMobile();
  const isAdminPanel = useIsAdminPanel();
  const navigate = useNavigate();
  const showStatusBar = useStatusBarDisplayed();

  const [openNavSection, setopenNavSection] = useState("");
  const [navSectionWasClosed, setnavSectionWasClosed] = useState("");

  const toggleDrawer = () => {
    open && setopenNavSection("");
    setOpen(!open);
  };

  const toggleParentNavItem = (navItemKey, navItemIsOpen) => {
    if (!open) return;

    if (openNavSection === navItemKey || navItemIsOpen) {
      setopenNavSection("");
      setnavSectionWasClosed(navItemKey);
    } else {
      setopenNavSection(navItemKey);
      setnavSectionWasClosed("");
    }
  };

  const handleClick = (navItem, navItemIsOpen) => () => {
    const onlyChild = hasOnlyChild(navItem) ? navItem.children[0] : null;
    const collapsesOnlyChild = Boolean(onlyChild) && open && navItemIsOpen;

    if (navItem.children) {
      toggleParentNavItem(navItem.key, navItemIsOpen);
      if (!onlyChild) trackEvent(ANALYTICS_EVENTS.GENERAL.OPEN_MENU, { destination: navItem.text });
    }

    const destination = onlyChild ?? navItem;
    if (destination.url && !collapsesOnlyChild) {
      navigate(destination.url);
      trackEvent(ANALYTICS_EVENTS.GENERAL.PAGE_NAV, { destination: destination.text, trigger: "navPage" });
    }
    if (isMobile) setOpen(false);
  };

  const singleItemProps = {
    handleClick,
    open,
    openNavSection,
    navSectionWasClosed,
    currentPage
  };

  return (
    <>
      {systemMode === SYSTEM_MODES.PREVIEW ? <div style={{
        height: `calc(100% - ${APP_HEADER_HEIGHT}px)`,
        borderRight: "1px solid #92929238",
        zIndex: 1,
        position: "absolute",
        bottom: 0,
        left: 0,
        width: "100%"
      }} /> : null}
      <div style={{ marginTop: (isMobile ? APP_MOBILE_HEADER_HEIGHT : APP_HEADER_HEIGHT) + (showStatusBar ? APP_STATUS_BAR_HEIGHT : 0) }} />
      {showAssistantSelector && <>
        <AssistantSelector isNavPanelOpen={open} />
        <Divider sx={{ margin: "0 20px 8px 16px", borderColor: theme => alpha(theme.palette.text[800], 0.12) }} />
      </>}
      {showMinimizeButton && systemMode !== SYSTEM_MODES.PREVIEW
        ? (
          <ChevronIcon
            statusBarDisplayed={showStatusBar}
            size="small"
            onClick={toggleDrawer}
            aria-label={open ? NAVIGATION_LABELS.collapseNavigation : NAVIGATION_LABELS.expandNavigation}
            aria-expanded={open}
            data-testid={NAVIGATION_TEST_IDS.collapseToggle}
          >
            {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </ChevronIcon>
        )
        : null}
      <List
        sx={{ pt: isAdminPanel || !showAssistantSelector ? 1 : 0, overflowY: "auto", overflowX: "hidden" }}
      >
        {sections?.map((section, sectionIndex) => {
          const showSectionHeader = section.title && (open || sectionIndex > 0);
          const isCollapsible = Boolean(section.collapsible) && open;
          const isSectionCollapsed = isCollapsible && collapsedSections.includes(section.key);
          const sectionItems = section.items.map(navItem => (navItem.isLoading
            ? <NavItemSkeleton key={navItem.key} open={open} />
            : <NavItem key={navItem.key} navItem={navItem} {...singleItemProps} />
          ));

          return (
            <Fragment key={section.key}>
              {showSectionHeader && (
                <TextDivider open={open} isFirst={sectionIndex === 0}>
                  {!open && <CollapsedDivider />}
                  {open && (isCollapsible
                    ? (
                      <SectionToggle
                        aria-expanded={!isSectionCollapsed}
                        aria-controls={NAVIGATION_DOM_IDS.sectionRegion(section.key)}
                        onClick={() => toggleSection(section.key)}
                        data-testid={NAVIGATION_TEST_IDS.sectionToggle(section.key)}
                      >
                        {isSectionCollapsed ? <ChevronRightIcon /> : <ExpandMore />}
                        <SectionTitle variant="overline">{section.title}</SectionTitle>
                      </SectionToggle>
                    )
                    : <SectionTitle variant="overline">{section.title}</SectionTitle>)}
                </TextDivider>
              )}
              {isCollapsible
                ? (
                  <Collapse
                    id={NAVIGATION_DOM_IDS.sectionRegion(section.key)}
                    in={!isSectionCollapsed}
                    component="li"
                    timeout="auto"
                    unmountOnExit
                  >
                    <List disablePadding>{sectionItems}</List>
                  </Collapse>
                )
                : sectionItems}
            </Fragment>
          );
        })}
      </List>
    </>
  );
};

export default DrawerContent;
